import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Lead } from '@/types/lead';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Button } from '@/components/ui/button';
import { Star, Trash2, FileText, MessageSquareText, Phone, Mail, Globe, Table2 } from 'lucide-react';
import { format } from 'date-fns';
import { Badge } from '@/components/ui/badge';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import DeleteConfirmationDialog from './DeleteConfirmationDialog';
import { useAuth } from '@/context/AuthContext';
import { formatEmailForDisplay, getPrimaryEmail } from '@/utils/emailUtils';

interface TableViewProps {
  leads: Lead[];
  onToggleFavorite: (id: string) => void;
  onDelete: (id: string) => void;
}

const TableView: React.FC<TableViewProps> = ({
  leads,
  onToggleFavorite,
  onDelete,
}) => {
  const navigate = useNavigate();
  const { user } = useAuth();

  const handleRowClick = (leadId: string) => {
    navigate(`/leads/${leadId}`);
  };

  return (
    <>
      {/* Mobile message - suggest switching to card/list view */}
      <div className="md:hidden p-6 border rounded-md bg-muted/30 text-center">
        <Table2 className="h-12 w-12 mx-auto mb-3 text-muted-foreground" />
        <h3 className="font-medium mb-2">Table View Not Available</h3>
        <p className="text-sm text-muted-foreground mb-4">
          Table view is optimized for larger screens. Please switch to Card or List view for a better mobile experience.
        </p>
      </div>

      {/* Desktop table view */}
      <div className="hidden md:block overflow-x-auto rounded-xl border border-border/60 shadow-sm bg-background">
        <Table>
        <TableHeader className="bg-gradient-to-r from-muted/90 to-muted/80 backdrop-blur-md sticky top-0 z-10 shadow-sm">
          <TableRow>
            <TableHead className="w-[50px] px-4 py-4 font-semibold text-sm tracking-wide"></TableHead>
            <TableHead className="px-4 py-4 font-semibold text-sm tracking-wide">Company</TableHead>
            <TableHead className="px-4 py-4 font-semibold text-sm tracking-wide">Contact</TableHead>
            <TableHead className="px-4 py-4 font-semibold text-sm tracking-wide">Address</TableHead>
            <TableHead className="px-4 py-4 font-semibold text-sm tracking-wide">Rating</TableHead>
            <TableHead className="px-4 py-4 font-semibold text-sm tracking-wide">Category</TableHead>
            <TableHead className="px-4 py-4 font-semibold text-sm tracking-wide">Notes/Docs</TableHead>
            <TableHead className="w-[100px] px-4 py-4 font-semibold text-sm tracking-wide text-right">Actions</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {leads.map((lead, idx) => (
            <TableRow 
              key={lead.id}
              onClick={() => handleRowClick(lead.id)}
              className={`border-b border-border/40 hover:bg-primary/5 hover:shadow-sm transition-all duration-200 cursor-pointer ${idx % 2 === 0 ? 'bg-muted/90' : 'bg-background'}`}
            >
              <TableCell className="px-4 py-4" onClick={(e) => e.stopPropagation()}>
                <button
                  onClick={() => onToggleFavorite(lead.id)}
                  className="text-muted-foreground hover:text-yellow-500 transition-all hover:scale-110"
                  aria-label="Toggle favorite"
                >
                  <Star className={lead.isFavorite ? "h-5 w-5 fill-yellow-500 text-yellow-500" : "h-5 w-5"} />
                </button>
              </TableCell>
              <TableCell className="px-4 py-4">
                <div className="flex items-center space-x-3">
                  {lead.imageUrl && (
                    <div className="h-8 w-8 rounded-md overflow-hidden border border-border/20">
                      <img
                        src={lead.imageUrl}
                        alt={lead.name}
                        className="h-full w-full object-cover"
                      />
                    </div>
                  )}
                  <span className="font-semibold tracking-tight">{lead.name}</span>
                </div>
              </TableCell>
              <TableCell className="px-4 py-4">
                <div className="space-y-1.5 text-sm">
                  {lead.phone && (
                    <div className="flex items-center gap-1.5 hover:text-foreground transition-colors">
                      <Phone className="h-3.5 w-3.5 text-primary/70" />
                      <a href={`tel:${lead.phone}`} className="hover:text-primary transition-colors hover:underline underline-offset-2" onClick={(e) => e.stopPropagation()}>
                        {lead.phone}
                      </a>
                    </div>
                  )}
                  {lead.email && (
                    <div className="flex items-center gap-1.5 hover:text-foreground transition-colors">
                      <Mail className="h-3.5 w-3.5 text-primary/70" />
                      <a href={`mailto:${getPrimaryEmail(lead.email)}`} className="hover:text-primary transition-colors truncate max-w-[150px] hover:underline underline-offset-2" onClick={(e) => e.stopPropagation()}>
                        {formatEmailForDisplay(lead.email)}
                      </a>
                    </div>
                  )}
                  {lead.website && (
                    <div className="flex items-center gap-1.5 hover:text-foreground transition-colors">
                      <Globe className="h-3.5 w-3.5 text-primary/70" />
                      <a href={lead.website} target="_blank" rel="noopener noreferrer" className="hover:text-primary transition-colors truncate max-w-[150px] hover:underline underline-offset-2" onClick={(e) => e.stopPropagation()}>
                        {lead.website?.replace(/^https?:\/\//, '')}
                      </a>
                    </div>
                  )}
                </div>
              </TableCell>
              <TableCell className="px-4 py-4">
                <div className="text-sm text-muted-foreground/80 truncate max-w-[200px]">{lead.address}</div>
              </TableCell>
              <TableCell className="px-4 py-4">
                {lead.rating ? (
                  <Badge variant="secondary" className="text-xs whitespace-nowrap bg-gradient-to-r from-yellow-500/10 to-orange-500/10 border-yellow-500/20">
                    <Star className="h-3 w-3 mr-1 fill-yellow-500 text-yellow-500" />
                    {lead.rating}
                  </Badge>
                ) : (
                  <span className="text-sm text-muted-foreground">-</span>
                )}
              </TableCell>
              <TableCell className="px-4 py-4">
                {lead.category ? (
                  <Badge variant="outline" className="text-xs border-primary/20 bg-gradient-to-r from-primary/10 to-primary/5">
                    {lead.category}
                  </Badge>
                ) : (
                  <span className="text-sm text-muted-foreground">-</span>
                )}
              </TableCell>
              <TableCell>
                <div className="flex items-center space-x-2">
                  {lead.notes.length > 0 && (
                    <TooltipProvider>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <div className="flex items-center">
                            <MessageSquareText className="h-4 w-4 mr-1" />
                            <span className="text-sm">{lead.notes.length}</span>
                          </div>
                        </TooltipTrigger>
                        <TooltipContent>
                          <div className="space-y-1">
                            <p className="font-medium text-sm">Notes</p>
                            <ul className="text-xs space-y-1">
                              {lead.notes.map((note) => (
                                <li key={note.id}>
                                  {note.content.length > 50 
                                    ? `${note.content.substring(0, 50)}...` 
                                    : note.content}
                                </li>
                              ))}
                            </ul>
                          </div>
                        </TooltipContent>
                      </Tooltip>
                    </TooltipProvider>
                  )}
                  
                  {lead.documents.length > 0 && (
                    <TooltipProvider>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <div className="flex items-center">
                            <FileText className="h-4 w-4 mr-1" />
                            <span className="text-sm">{lead.documents.length}</span>
                          </div>
                        </TooltipTrigger>
                        <TooltipContent>
                          <div className="space-y-1">
                            <p className="font-medium text-sm">Documents</p>
                            <ul className="text-xs space-y-1">
                              {lead.documents.map((doc) => (
                                <li key={doc.id}>
                                  <a 
                                    href={doc.url} 
                                    target="_blank" 
                                    rel="noopener noreferrer"
                                    className="hover:underline"
                                  >
                                    {doc.name}
                                  </a>
                                </li>
                              ))}
                            </ul>
                          </div>
                        </TooltipContent>
                      </Tooltip>
                    </TooltipProvider>
                  )}
                </div>
              </TableCell>
              <TableCell className="px-4 py-4 text-right" onClick={(e) => e.stopPropagation()}>
                {lead.createdBy === user?.id && (
                  <DeleteConfirmationDialog 
                    entityName={lead.name}
                    onConfirm={() => onDelete(lead.id)}
                    trigger={
                      <Button
                        variant="destructive"
                        size="sm"
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    }
                  />
                )}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
      </div>
    </>
  );
};

export default TableView;
